import { rufe } from './client';

/**
 * Der Trainerkalender (RES-27 bis RES-33).
 *
 * ============================================================================
 * KEIN AUFRUF DIESER DATEI NENNT EINE RESSOURCE
 * ============================================================================
 *
 * RES-32: „Ein Dienstleister sieht ausschliesslich eigene Daten. Die
 * Sichtbarkeitsprüfung erfolgt serverseitig, nicht durch Ausblenden in der
 * Oberfläche."
 *
 * Die Ressource steht im angemeldeten Konto, und der Server nimmt sie von dort.
 * Gäbe es hier einen Bezeichner mitzugeben, wäre die Prüfung eine, die der
 * Aufrufer selbst vornimmt — und damit keine.
 */

/**
 * Ein Einsatz, wie der Kalender ihn zeigt (RES-29).
 *
 * Fünf Angaben, die RES-29 aufzählt, dazu der Stand und die Frist. Was NICHT
 * dabeisteht, steht in RES-31: keine Kundendaten, keine Preise, keine
 * Teilnehmer. Diese Liste ist deshalb abschließend; wer sie erweitert,
 * erweitert eine Datenfreigabe an einen Dritten.
 */
export type Einsatz = {
  readonly belegung: string;
  readonly termin: string;
  /** Der Geschäftstag als JJJJ-MM-TT in Ortszeit (FRS-01). */
  readonly tag: string;
  /** HH:MM in Ortszeit — mit Rüst- und Nachbereitungszeit (RES-06). */
  readonly von: string;
  readonly bis: string;
  readonly dauerStunden: number;
  readonly ort: string;
  /** Das Training, nicht der Kunde (RES-31). */
  readonly leistung: string;
  readonly stand: string;
  /** Offene Anfrage mit Frist: Hier wird eine Antwort erwartet (RES-33). */
  readonly angefragt: boolean;
  /** UTC nach ISO 8601; die Anzeige rechnet in Ortszeit um (LOG-03). */
  readonly frist: string | null;
  /** RES-31: Ob die Durchführungsangaben zu diesem Termin schon frei sind. */
  readonly detailsFrei: boolean;
  /**
   * Was zu diesem Einsatz zurückgemeldet wurde (RES-77, TAG-43).
   *
   * `null` heißt: noch nichts gemeldet. Das ist etwas anderes als
   * „wie beauftragt" — dort hat jemand hingesehen und nichts gefunden.
   */
  readonly rueckmeldung: Rueckmeldung | null;
};

/** Die vier Abweichungen aus RES-77, dazu „lief wie beauftragt". */
export const RUECKMELDUNGSARTEN = [
  'wie_beauftragt',
  'verkuerzt',
  'verlaengert',
  'ausgefallen',
  'vertreten',
] as const;

export type Rueckmeldungsart = (typeof RUECKMELDUNGSARTEN)[number];

/** Wie die Arten am Bildschirm heißen — die Reihenfolge ist die von RES-77. */
export const RUECKMELDUNGSNAME: Record<Rueckmeldungsart, string> = {
  wie_beauftragt: 'Lief wie geplant',
  verkuerzt: 'Kürzer als geplant',
  verlaengert: 'Länger als geplant',
  ausgefallen: 'Fiel aus',
  vertreten: 'Jemand anders ist gefahren',
};

export type Rueckmeldung = {
  readonly art: Rueckmeldungsart;
  /** HH:MM in Ortszeit; `null` außer bei kürzer und länger. */
  readonly vonZeit: string | null;
  readonly bisZeit: string | null;
  readonly bemerkung: string | null;
  readonly gemeldetAm: string;
};

export type Eigene = {
  readonly ressource: string;
  readonly bezeichnung: string;
  readonly standort: string | null;
};

/** Wer angemeldet ist, aus Sicht des Kalenders (RES-27). */
export async function ich(): Promise<Eigene> {
  return rufe('/trainer/ich');
}

/**
 * Die eigenen Einsätze (RES-29).
 *
 * Ohne Zeitraum kommt das Fenster, das der Server setzt: ein Monat zurück, ein
 * Quartal voraus. Die Maske gibt ihn deshalb in der Regel nicht mit.
 */
export async function einsaetze(
  von?: string,
  bis?: string,
): Promise<{ readonly einsaetze: readonly Einsatz[] }> {
  const felder = new URLSearchParams();
  if (von !== undefined) felder.set('von', von);
  if (bis !== undefined) felder.set('bis', bis);
  const anhang = felder.toString();
  return rufe(`/trainer/einsaetze${anhang === '' ? '' : `?${anhang}`}`);
}

/**
 * Zusagen oder absagen (RES-33).
 *
 * Der Grund ist bei einer Absage erwünscht und nie Pflicht: Die Disposition
 * braucht die Absage schnell, damit sie weiterfragen kann (RES-49).
 */
export async function antworten(
  belegung: string,
  zusage: boolean,
  grund?: string,
): Promise<{ readonly stand: string }> {
  return rufe(`/trainer/einsaetze/${encodeURIComponent(belegung)}/antwort`, {
    verfahren: 'POST',
    rumpf: { zusage, ...(grund === undefined || grund === '' ? {} : { grund }) },
  });
}

/**
 * Ein Termin im Tagesbild (RES-27 i.d.F. ÄA-20).
 *
 * ============================================================================
 * HIER STEHT MEHR ALS IN `Einsatz` — UND ZWAR ABSICHTLICH
 * ============================================================================
 *
 * Der Auftraggeber am 11.09.2026: „Ein Trainer soll den ganzen Tagesplan sehen
 * können, damit er weiß, was um ihn herum passiert." ÄA-20 schränkt RES-31 und
 * RES-32 dafür ein.
 *
 * Was trotzdem nicht dabeisteht: Preise, Deckungsbeiträge und
 * Teilnehmernamen. `teilnehmer` ist eine Zahl — namentlich wird es in der
 * Teilnehmerliste (TAG-36), und die bekommt der Trainer in die Hand, nicht an
 * die Wand.
 */
export type Tagesbildtermin = {
  readonly termin: string;
  readonly produkt: string;
  readonly artikelnummer: string;
  readonly beginn: string;
  readonly ende: string;
  /** BUC-66: `offen` (Einzelplätze) oder `exklusiv` (einem Kunden ganz). */
  readonly terminart: string;
  /** Der Kunde eines Exklusivtermins; `null` beim offenen Termin (ÄA-20). */
  readonly kundenname: string | null;
  /** Die Zahl der Teilnehmer, nicht ihre Namen (TAG-36). */
  readonly teilnehmer: number;
  readonly trainer: readonly string[];
  /** Ob ich an diesem Termin eingeplant bin. */
  readonly meiner: boolean;
  readonly ablauf: readonly {
    readonly von: string;
    readonly bis: string;
    readonly was: string;
    readonly ort: string | null;
  }[];
};

export type Tagesbild = {
  readonly tag: string;
  readonly standort: string;
  readonly standortname: string;
  /** TAG-33: Entwurf, freigegeben, in Durchführung, abgeschlossen. */
  readonly stand: string;
  readonly freigegeben: boolean;
  readonly termine: readonly Tagesbildtermin[];
};

/**
 * Der ganze Tag des eigenen Standorts (ÄA-20).
 *
 * Ohne `tag` der heutige Geschäftstag — den rechnet der Server in Ortszeit aus
 * (FRS-01) und nicht der Browser: Wer um eins nachts nachsieht, meint den Tag,
 * der gerade zu Ende ging.
 *
 * `null` heißt: An diesem Tag gibt es keinen Betriebstag. Das ist kein Fehler,
 * sondern „hier steht nichts an".
 */
export async function tagesbild(tag?: string): Promise<{ readonly tagesbild: Tagesbild | null }> {
  const anhang = tag === undefined ? '' : `?tag=${encodeURIComponent(tag)}`;
  return rufe(`/trainer/tagesbild${anhang}`);
}

/**
 * Zurückmelden, was aus dem Einsatz geworden ist (RES-77, TAG-43).
 *
 * Der Rückweg, den es bis zum 13.09.2026 nicht gab: Der Tag wurde geplant,
 * besetzt und freigegeben — und was daraus wurde, blieb am Platz. RES-77 lässt
 * die vier Abweichungen „auf die Abrechnungsgrundlage wirken"; ohne diesen Weg
 * wirkt nichts.
 *
 * Die Zeiten sind HH:MM in Ortszeit. Welcher Tag gemeint ist, weiß der Server
 * aus der Belegung — eine Umrechnung im Browser wäre eine Stelle mehr, an der
 * die Ortszeit verlorengeht (FRS-01).
 */
export async function rueckmelden(
  belegung: string,
  eingabe: {
    readonly art: Rueckmeldungsart;
    readonly vonZeit?: string;
    readonly bisZeit?: string;
    readonly bemerkung?: string;
  },
): Promise<{ readonly gemeldet: true }> {
  return rufe(`/trainer/einsaetze/${encodeURIComponent(belegung)}/rueckmeldung`, {
    verfahren: 'POST',
    rumpf: eingabe,
  });
}

/**
 * Ein Teilnehmer in der Liste des eigenen Kurses (TAG-36, TAG-42 i.d.F. ÄA-24).
 *
 * PLATZ UND NAME — und sonst nichts. Keine E-Mail, kein Geburtsdatum, kein
 * Preis, keine Buchungsnummer (RES-31). Und kein Haken: Der Auftraggeber am
 * 13.09.2026: „der trainer soll die teilnehmer nicht abhaken." Die Anwesenheit
 * erfasst der Empfang.
 */
export type Gruppenteilnehmer = {
  readonly teilnehmer: string;
  /** Die laufende Nummer der Buchung — „Platz 3 von 5" (BUC-69). */
  readonly platz: number;
  /** `null`, solange kein Name gemeldet ist. Dann klärt ihn der Empfang (TAG-73). */
  readonly name: string | null;
};

/**
 * Die Teilnehmer eines eigenen Termins (TAG-36, TAG-42).
 *
 * Drei Schranken stehen im Server und nicht hier: eigener Einsatz an diesem
 * Termin, Vereinbarung nach RES-39, Freigabefenster nach RES-31. Ein fremder
 * Termin ist „nicht gefunden" — die Maske erfährt nicht, dass es ihn gibt.
 */
export async function teilnehmer(
  termin: string,
): Promise<{ readonly teilnehmer: readonly Gruppenteilnehmer[] }> {
  return rufe(`/trainer/termine/${encodeURIComponent(termin)}/teilnehmer`);
}

/**
 * Eine Nachricht an die eigene Ressource (TAG-34) — etwa: der Plan hat sich
 * nach der Freigabe geändert. Text und Zeit, sonst nichts.
 */
export type Trainernachricht = {
  readonly id: string;
  readonly text: string;
  /** UTC nach ISO 8601; die Anzeige rechnet in Ortszeit um (LOG-03). */
  readonly entstandenAm: string;
};

/** Die ungelesenen Nachrichten an mich als Trainer (TAG-34). */
export async function benachrichtigungen(): Promise<{ readonly nachrichten: readonly Trainernachricht[] }> {
  return rufe('/trainer/benachrichtigungen');
}

/** Gelesen — danach steht sie nicht mehr im Kalender (TAG-34). */
export async function benachrichtigungGelesen(id: string): Promise<void> {
  await rufe(`/trainer/benachrichtigungen/${encodeURIComponent(id)}/gelesen`, { verfahren: 'POST' });
}
